import {
  FUNDED_BY_NAME_MAX_LENGTH,
  ImpactGroup,
  InputVariantTypes,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import { useState } from "react";
import { StatusTag } from "../StatusTag";
import { TextField, TextFieldChangeEvent } from "../TextField";
import MinusIcon from "@ecologi/assets/icons/minus-icon.svg";
import PlusIcon from "@ecologi/assets/icons/plus-basic-icon.svg";
import { GroupSelector } from "./GroupSelector";

const TagImpact = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.mist};
`;

const TagImpactHeader = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;

  &:focus-visible {
    outline: ${({ theme }) => `2px solid ${theme.colors.forest}`};
    outline-offset: 2px;
  }
`;

const TagImpactTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TagImpactHeaderText = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.charcoal};
`;

const TagImpactContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 24px;
`;

const TagImpactIcon = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export type TagImpactSectionProps = {
  fundedBy: string;
  groupId: number | null;
  groups: ImpactGroup[];
  isLoggedIn: boolean;
  isOutOfStock: boolean;
  onCreateGroup: (name: string) => Promise<ImpactGroup>;
  onFundedByChange: (value: string) => void;
  onGroupChange: (groupId: number | null) => void;
};

export const TagImpactSection = ({
  fundedBy,
  groupId,
  groups,
  isLoggedIn,
  isOutOfStock,
  onCreateGroup,
  onFundedByChange,
  onGroupChange,
}: TagImpactSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFundedByChange = (e: TextFieldChangeEvent) => {
    onFundedByChange(e.target.value);
  };

  return (
    <TagImpact>
      <TagImpactHeader
        onClick={() => setIsExpanded(!isExpanded)}
        disabled={isOutOfStock}
        data-testid="tag-impact-header"
      >
        <TagImpactTitle>
          <TagImpactHeaderText>Tag this impact</TagImpactHeaderText>
          <StatusTag label="New" variant="feature" rounded={false} />
        </TagImpactTitle>
        <TagImpactIcon>
          {isExpanded ? <MinusIcon /> : <PlusIcon />}
        </TagImpactIcon>
      </TagImpactHeader>
      {isExpanded && (
        <TagImpactContent>
          <TextField
            data-testid="funded-by-input"
            helpText="Who this impact is funded for"
            label="Funded by (optional)"
            isDisabled={isOutOfStock}
            name="fundedBy"
            placeholder="Default"
            onChange={handleFundedByChange}
            maxLength={FUNDED_BY_NAME_MAX_LENGTH}
            showCharacterCount={fundedBy.length > 0}
            variant={InputVariantTypes.SNOWDROP}
            value={fundedBy}
          />
          <GroupSelector
            groupId={groupId}
            groups={groups}
            isDisabled={isOutOfStock}
            isLoggedIn={isLoggedIn}
            onCreateGroup={onCreateGroup}
            onGroupChange={onGroupChange}
            variant={InputVariantTypes.SNOWDROP}
          />
        </TagImpactContent>
      )}
    </TagImpact>
  );
};
